import { useState, useRef, useEffect } from "react";

// Searchable dropdown — same props/API as the old plain <select> wrapper
// (label, value, onChange, options, required) so every existing usage
// gets a type-to-search box for free, with no call-site changes needed.
export default function Select({
  label,
  value,
  onChange,
  options,
  required = false,
  placeholder = "-- اختر --",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const selected = options.find((o) => String(o.value) === String(value));
  const q = query.trim().toLowerCase();
  const filtered = q
    ? options.filter((o) => String(o.label).toLowerCase().includes(q))
    : options;

  const selectOption = (v) => {
    onChange(v === "" ? "" : String(v));
    setOpen(false);
    setQuery("");
  };

  return (
    <div
      ref={wrapRef}
      style={{ display: "flex", flexDirection: "column", gap: 6 }}
    >
      {label && (
        <label style={{ color: "#94a3b8", fontSize: 13 }}>
          {label}
          {required && <span style={{ color: "#ef4444" }}> *</span>}
        </label>
      )}
      <div style={{ position: "relative" }}>
        {open ? (
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                setOpen(false);
                setQuery("");
              } else if (e.key === "Enter" && filtered.length) {
                e.preventDefault();
                selectOption(filtered[0].value);
              }
            }}
            placeholder="بحث..."
            style={inputStyle}
          />
        ) : (
          <div
            onClick={() => setOpen(true)}
            style={{
              ...inputStyle,
              cursor: "pointer",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 8,
            }}
          >
            <span
              style={{
                color: selected ? "#f1f5f9" : "#64748b",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {selected ? selected.label : placeholder}
            </span>
            <span style={{ color: "#64748b", fontSize: 11, flexShrink: 0 }}>▾</span>
          </div>
        )}
        {open && (
          <div style={dropdownStyle}>
            <div onClick={() => selectOption("")} style={optionStyle(!value)}>
              {placeholder}
            </div>
            {filtered.length === 0 ? (
              <div style={{ padding: "10px 13px", color: "#475569", fontSize: 13 }}>
                لا توجد نتائج
              </div>
            ) : (
              filtered.map((o) => (
                <div
                  key={o.value}
                  onClick={() => selectOption(o.value)}
                  style={optionStyle(String(o.value) === String(value))}
                >
                  {o.label}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const inputStyle = {
  background: "#0f172a",
  border: "1px solid #334155",
  borderRadius: 10,
  padding: "10px 13px",
  color: "#f1f5f9",
  fontSize: 14,
  fontFamily: "inherit",
  outline: "none",
  width: "100%",
  boxSizing: "border-box",
};

const dropdownStyle = {
  position: "absolute",
  top: "calc(100% + 4px)",
  right: 0,
  left: 0,
  background: "#0f172a",
  border: "1px solid #334155",
  borderRadius: 10,
  maxHeight: 240,
  overflowY: "auto",
  zIndex: 50,
  boxShadow: "0 12px 28px #000a",
};

const optionStyle = (active) => ({
  padding: "9px 13px",
  fontSize: 13,
  color: active ? "#fff" : "#cbd5e1",
  background: active ? "#1d4ed8" : "transparent",
  cursor: "pointer",
});
